import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../../components/PageLayout';
import PageHero from '../../components/PageHero';

const BREADCRUMBS = [
  { label: 'Home', to: '/' },
  { label: 'Our Story', to: '/our-story/ifys-story' },
  { label: 'Our People' },
];

const JoinTeamCircle = () => (
  <Link
    to="/employment/ifys-recruitment"
    aria-label="Want to join our team? Visit IFYS Careers"
    className="inline-block rounded-full transition-transform duration-200 hover:scale-[1.04] focus:outline-none focus-visible:ring-4 focus-visible:ring-white/70"
  >
    <img
      src="/images/buttons/want-to-join-our-team.png"
      alt="Want to join our team?"
      className="block w-20 h-20 lg:w-24 lg:h-24 xl:w-28 xl:h-28 select-none"
      draggable={false}
    />
  </Link>
);

const JoinTeamMobile = () => (
  <Link
    to="/employment/ifys-recruitment"
    aria-label="Want to join our team? Visit IFYS Careers"
    className="inline-block rounded-full transition-transform duration-200 hover:scale-[1.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-ifys-blue focus-visible:ring-offset-2"
  >
    <img
      src="/images/buttons/want-to-join-our-team.png"
      alt="Want to join our team?"
      className="block w-24 h-24 sm:w-28 sm:h-28 select-none"
      draggable={false}
    />
  </Link>
);

const COMMITMENTS = [
  {
    title: 'Diversity & Respect',
    description: 'We value and respect the diversity of our staff, foster carers, and volunteers.',
  },
  {
    title: 'Collaboration',
    description: 'Our leaders foster collaboration and harness the collective knowledge and experience of our people.',
  },
  {
    title: 'Growth & Potential',
    description: 'Our people are supported and encouraged to fulfil their potential.',
  },
  {
    title: 'Accountability',
    description: 'Leaders are accountable for setting the standard and promoting contemporary and innovative practice.',
  },
  {
    title: 'Training & Development',
    description: 'We are committed to providing tailored training to empower, focus and engage our people.',
  },
  {
    title: 'Safety',
    description: 'Our staff, foster carers and volunteers will be afforded safe systems of work.',
  },
  {
    title: 'Resilience',
    description: 'We will adapt and evolve through strengthening the resilience of our teams.',
  },
];

const OurPeoplePage = () => {
  return (
    <PageLayout activeLink="our-story">
      <PageHero
        title="Our People"
        backgroundImage="/images/thumb2.jpg"
        breadcrumbs={BREADCRUMBS}
        variant="split"
        childrenPosition="image-left"
      >
        <JoinTeamCircle />
      </PageHero>

      <section className="md:hidden bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5 sm:py-6 flex justify-center">
          <JoinTeamMobile />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <h2 className="text-3xl sm:text-4xl font-bold text-ifys-blue tracking-tight leading-tight">
            Our People
          </h2>

          <div className="mt-8 pt-8 border-t border-gray-200 space-y-5 text-gray-700 text-base sm:text-lg leading-relaxed">
            <p>
              For over forty years, IFYS has pursued opportunities that create change, develop
              awareness, and strengthen the human spirit.
            </p>
            <p>
              Our vision is to research, educate, and provide support services that assist people in
              need to reach their full potential and participate in a just society.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-ifys-light-gray border-y border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
          <figure className="border-l-4 border-ifys-blue pl-6 sm:pl-8">
            <blockquote className="text-lg sm:text-xl text-ifys-blue leading-relaxed font-medium">
              We recognise that to achieve our vision, it&rsquo;s our people that make the difference.
            </blockquote>
          </figure>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="max-w-3xl mb-10 sm:mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-ifys-blue tracking-tight">
              Our commitments
            </h3>
            <p className="mt-2 text-base sm:text-lg text-gray-600 leading-relaxed">
              To promote a culture of excellence we ensure&hellip;
            </p>
          </div>

          <ol className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
            {COMMITMENTS.map(({ title, description }, index) => (
              <li
                key={title}
                className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-7"
              >
                <div className="flex items-start gap-5">
                  <span
                    aria-hidden="true"
                    className="flex-shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-full bg-ifys-blue text-white font-bold text-sm tracking-wider"
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div className="min-w-0">
                    <h4 className="text-base sm:text-lg font-bold text-ifys-blue mb-2 leading-tight">
                      {title}
                    </h4>
                    <p className="text-sm sm:text-[15px] text-gray-600 leading-relaxed">
                      {description}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-ifys-light-gray border-t border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
          <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
            We&rsquo;re about people &mdash; this means not only supporting those who access our
            services to thrive, but also our dedicated and passionate staff. Whether it&rsquo;s
            excellent work/life balance, continuous professional development, or looking to work in
            an inspiring environment, IFYS has opportunities for everyone to excel.
          </p>
        </div>
      </section>
    </PageLayout>
  );
};

export default OurPeoplePage;
